import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Button } from '@/components/ui/button'
import { Field, FieldContent, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'

const normalizeToSlug = (value: string): string =>
	value
		.trim()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')

const buildInternalName = (value: string): string => normalizeToSlug(value)

const buildCode = (value: string): string => {
	const slug = normalizeToSlug(value).toUpperCase().substring(0, 16)
	return slug ? `SYS-${slug}` : ''
}

export interface SystemBranchFormData {
	name: string
	code: string
	customName: string
}

interface SystemBranchFormProps {
	onSave: (data: SystemBranchFormData) => void
	onClose: () => void
}

export function SystemBranchFormContent({
	onSave,
	onClose,
}: SystemBranchFormProps) {
	const { t } = useTranslation()
	const [name, setName] = useState('')
	const [code, setCode] = useState('')
	const [customName, setCustomName] = useState('')
	const [nameManuallyEdited, setNameManuallyEdited] = useState(false)
	const [codeManuallyEdited, setCodeManuallyEdited] = useState(false)

	const handleCustomNameChange = (value: string) => {
		setCustomName(value)

		if (!nameManuallyEdited) {
			setName(buildInternalName(value))
		}

		if (!codeManuallyEdited) {
			setCode(buildCode(value))
		}
	}

	const handleSave = () => {
		if (!name.trim()) {
			toast.error(t('administration.orgOverrides.form.emptyNameError'))
			return
		}
		if (!code.trim()) {
			toast.error(t('administration.orgOverrides.form.emptyCodeError'))
			return
		}
		onSave({
			name: name.trim(),
			code: code.trim().toUpperCase(),
			customName: customName.trim(),
		})
	}

	return (
		<div className="flex flex-col gap-5 px-1">
			{/* Custom Name */}
			<Field>
				<FieldLabel htmlFor="sys-branch-customname">
					{t('administration.orgOverrides.form.customNameLabel')}
				</FieldLabel>
				<FieldContent>
					<Input
						id="sys-branch-customname"
						value={customName}
						onChange={(e) => handleCustomNameChange(e.target.value)}
						placeholder={t(
							'administration.orgOverrides.form.customNamePlaceholder',
						)}
						maxLength={100}
					/>
				</FieldContent>
			</Field>

			<Separator className="bg-border/60" />

			{/* Internal Slug */}
			<Field>
				<FieldLabel htmlFor="sys-branch-name">
					{t('administration.orgOverrides.form.internalNameLabel')}
				</FieldLabel>
				<FieldContent>
					<Input
						id="sys-branch-name"
						value={name}
						onChange={(e) => {
							setNameManuallyEdited(true)
							setName(e.target.value)
						}}
						placeholder={t(
							'administration.orgOverrides.form.internalNamePlaceholder',
						)}
						maxLength={100}
					/>
				</FieldContent>
			</Field>

			{/* Code */}
			<Field>
				<FieldLabel htmlFor="sys-branch-code">
					{t('administration.orgOverrides.form.codeLabel')}
				</FieldLabel>
				<FieldContent>
					<Input
						id="sys-branch-code"
						value={code}
						onChange={(e) => {
							setCodeManuallyEdited(true)
							setCode(e.target.value.toUpperCase())
						}}
						placeholder="SYS-SEG"
						maxLength={20}
					/>
					<p className="mt-1 text-xs text-muted-foreground">
						{t('administration.orgOverrides.form.codeHint')}
					</p>
				</FieldContent>
			</Field>

			<Separator className="bg-border/60" />

			{/* Actions */}
			<div className="flex gap-3 pb-1">
				<Button
					type="button"
					variant="cancel"
					onClick={onClose}
					className="flex-1"
				>
					{t('administration.orgOverrides.form.cancelButton')}
				</Button>
				<Button
					type="button"
					onClick={handleSave}
					className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
				>
					{t('administration.orgOverrides.form.createBranchButton')}
				</Button>
			</div>
		</div>
	)
}
